import { makeAutoObservable, observable } from 'mobx';
import React from 'react';

import { Cookies } from 'core/cookies';
import { Permission } from 'framework/permission';
import { MonitorType, MonitorUiType } from 'areas/assets/monitors/monitorType';
import { LoginResponse } from 'core/api';
import { getUrlBuilderForCurrentHash } from 'tools/urlBuilder';
import { fromLegacyFormat } from 'tools/dateTime/fromLegacyFormat';
import { parseDateFormat } from 'tools/dateTime/parseDateFormat';

// TODO: add other fields
export interface NotificationCounters {
	unreadMessages?: number;
	unreadSystemErrors?: number;
	eventSummaryCount?: number;
	lastChanges?: number;
}

class ApplicationStateInner {
	//this is not backend session. Backend session is stored in apiToken field
	//this is purely UI session to separate standalone instances from a default one
	sessionId: string;

	userName: string;
	userId: string;

	apiToken: string;
	accountId: string;
	accountName: string;
	accountLogoId: string;
	userPictureId: string;

	timezone: string;

	dateFormatLegacy: string;
	timeFormat: string;
	dateTimeFormatLegacy: string;

	locale: string;

	apiUrl: string = '/rest/';

	includeSubaccounts: boolean;
	includeSubaccountsMenuDisabled: boolean = false;

	standalone: boolean;

	enabledMonitors: {
		value: MonitorType;
		text: string;
		sharedAgents: boolean;
		uiType: MonitorUiType;
		monitorUrl?: string;
	}[] = [];

	userTags: string[] = [];
	ignoreTags: boolean;
	showUntagged: boolean;

	permissions: Permission[] = [];

	currentRouteId: string;
	currentHelpKey?: string;

	pageComponent: React.Component;

	notificationCounters: NotificationCounters = {};

	constructor() {
		makeAutoObservable(this, {
			pageComponent: observable.ref,
		});
	}

	get isGuestUser() {
		return this.userName == 'public';
	}

	get dateFormat() {
		return fromLegacyFormat(this.dateFormatLegacy);
	}

	get dateTimeFormat() {
		return fromLegacyFormat(this.dateTimeFormatLegacy);
	}

	get momentDateTimeFormat() {
		return parseDateFormat(this.dateTimeFormat).replace('.SSS', '').replace(':ss', '');
	}

	get momentDateTimeFormatFull() {
		return parseDateFormat(this.dateTimeFormat);
	}

	get tagsEnabled() {
		return this.userTags?.length > 0 && !this.ignoreTags;
	}

	loadStateFromCookies() {
		this.apiToken = Cookies.get('sessionId');
		this.accountId = Cookies.get('CeesoftCurrentAccountId');
		this.accountName = Cookies.get('CeesoftCurrentAccountName');
		this.dateFormatLegacy = Cookies.get('CeesoftUserDateFormat');
		this.timeFormat = Cookies.get('CeesoftUserTimeFormat');
		this.locale = Cookies.get('CeesoftUserLocale');
		this.dateTimeFormatLegacy = Cookies.get('CeesoftUserDateTimeFormat');
		this.timezone = Cookies.get('CeesoftTimezone');
		this.userName = Cookies.get('CeesoftUsername');
		this.userId = Cookies.get('CeesoftUserId');
		this.userPictureId = Cookies.get('CeesoftUserPictureId');
		this.accountLogoId = Cookies.get('CeesoftAccountLogoId');
	}

	loadStateFromLoginResponse(loginData: LoginResponse) {
		this.apiToken = loginData.sessionId;
		this.accountId = loginData.loginAccountId;
		this.accountName = loginData.loginAccountName;
		this.dateFormatLegacy = loginData.dateFormat;
		this.dateTimeFormatLegacy = loginData.dateTimeFormat;
		this.timeFormat = loginData.timeFormat;
		this.timezone = loginData.timeZone;
		this.locale = loginData.locale;
		this.userName = loginData.username;
		this.userId = loginData.userId;
		this.userPictureId = loginData.pictureId;
		this.accountLogoId = loginData.logoId;
	}

	hasPermissions = (...permissions: Permission[]) => {
		return permissions.every((p) => this.permissions.includes(p));
	};

	hasAnyPermission = (...permissions: Permission[]): boolean => {
		return permissions.some((p) => this.hasPermissions(p));
	};
}

export const ApplicationState = new ApplicationStateInner();

export function checkForIsolatedSession() {
	const urlBuilder = getUrlBuilderForCurrentHash();
	const sessionId = urlBuilder.params.sessionId;
	if (sessionId != null) {
		Cookies.useSessionStorage(sessionId);
		ApplicationState.sessionId = sessionId;
	}

	ApplicationState.standalone = urlBuilder.params.standalone == '1' || Cookies.CeesoftUsername == 'public';
}
